import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Volume2,
  VolumeX,
  Settings,
  Star,
  Heart,
  Trophy,
  Zap,
  Play,
  Home,
  Keyboard,
  Sparkles,
  Target,
  BookOpen,
  Pause,
  RotateCcw,
} from "lucide-react";

// Bootstrap colors for balloons
const COLORS = [
  "bg-danger", // red
  "bg-success", // green
  "bg-primary", // blue
  "bg-warning", // yellow
  "bg-info", // cyan
  "bg-purple", // purple
  "bg-pink", // pink
];

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

// Audio handling
const createAudioContext = () => {
  try {
    return new (window.AudioContext || window.webkitAudioContext)();
  } catch {
    return null;
  }
};

const playPopSound = (audioContext, volume) => {
  if (!audioContext) return;
  try {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(
      400,
      audioContext.currentTime + 0.1
    );
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(
      volume * 0.4,
      audioContext.currentTime + 0.01
    );
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContext.currentTime + 0.15
    );
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15);
  } catch (error) {
    console.log("Audio playback failed:", error);
  }
};

const BalloonLetterPop = () => {
  const [gameState, setGameState] = useState("menu");
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [lives, setLives] = useState(20);
  const [balloons, setBalloons] = useState([]);
  const [particles, setParticles] = useState([]);
  const [settings, setSettings] = useState({
    speed: 1, // Start with slower speed for kids
    volume: 0.7,
    soundEnabled: true,
    voiceEnabled: true,
    practiceMode: false,
  });
  const [showInstructions, setShowInstructions] = useState(true);
  const [cameFrom, setCameFrom] = useState("menu");
  const [isMobile, setIsMobile] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(true);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [showCombo, setShowCombo] = useState(false);
  const [comboPosition, setComboPosition] = useState({ x: 50, y: 50 });
  const [targetLetter, setTargetLetter] = useState(null);
  const [streak, setStreak] = useState(0);
  const [level, setLevel] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const audioContextRef = useRef(null);
  const balloonIdRef = useRef(0);
  const particleIdRef = useRef(0);
  const gameLoopRef = useRef(null);
  const initialHighRef = useRef(0);
  const lastSpawnTimeRef = useRef(0);
  const difficultyLevelRef = useRef(1);
  const comboTimeoutRef = useRef(null);
  const streakTimeoutRef = useRef(null);
  const balloonSpawnCountRef = useRef(1);
  const speechSynthesisRef = useRef(window.speechSynthesis || null);
  const gameContainerRef = useRef(null);

  useEffect(() => {
    const checkIfMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth < 768;
    };
    setIsMobile(checkIfMobile());
    
    const handleResize = () => {
      setIsMobile(checkIfMobile());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("balloonHighScore") || "0";
    const num = parseInt(saved, 10);
    setHighScore(num);
    initialHighRef.current = num;
  }, []);

  useEffect(() => {
    audioContextRef.current = createAudioContext();
    return () => {
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }
    };
  }, [settings.soundEnabled, settings.volume]);

  // Speak letter function
  const speakLetter = useCallback((letter) => {
    if (!settings.voiceEnabled || !speechSynthesisRef.current) return;
    
    try {
      const utterance = new SpeechSynthesisUtterance(letter);
      utterance.volume = settings.volume;
      utterance.rate = 0.8; // Slower for kids
      utterance.pitch = 1.2; // Slightly higher pitch for engagement
      speechSynthesisRef.current.speak(utterance);
    } catch (error) {
      console.log("Speech synthesis failed:", error);
    }
  }, [settings.voiceEnabled, settings.volume]);

  const resetGame = useCallback(() => {
    setScore(0);
    setLives(settings.practiceMode ? 999 : 20); // Infinite lives in practice mode
    setBalloons([]);
    setParticles([]);
    setCombo(0);
    setMaxCombo(0);
    setStreak(0);
    setLevel(1);
    setCountdown(3);
    setIsPaused(false);
    difficultyLevelRef.current = 1;
    balloonSpawnCountRef.current = 1;
    lastSpawnTimeRef.current = Date.now();
    
    // Start countdown
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setGameState("playing");
          setShowInstructions(true);
          setTimeout(() => setShowInstructions(false), 3000);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    const currentHigh = localStorage.getItem("balloonHighScore") || "0";
    initialHighRef.current = parseInt(currentHigh, 10);
  }, [settings.practiceMode]);

  const createBalloon = useCallback(() => {
    const letterOptions = [...LETTERS];
    if (targetLetter && Math.random() < 0.3) {
      // 30% chance to spawn the target letter
      letterOptions.push(targetLetter);
    }
    
    // Adjusted movement patterns for kids
    let movementPattern = "normal";
    let speedMultiplier = 1;
    
    if (level > 8) {
      // More gentle zigzag for kids
      movementPattern = "zigzag";
      speedMultiplier = 1.1;
    } else if (level > 4) {
      // Gentle wobble movement
      movementPattern = "wobble";
      speedMultiplier = 1.05;
    }
    
    const newBalloon = {
      id: balloonIdRef.current++,
      letter: letterOptions[Math.floor(Math.random() * letterOptions.length)],
      x: Math.random() * 70 + 15,
      y: -10,
      speed: (Math.random() * 0.2 + 0.3) * [0.5, 0.8, 1.2][settings.speed - 1] * 
             (1 + (difficultyLevelRef.current - 1) * 0.05) * speedMultiplier, // Reduced speed progression
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      wobble: Math.random() * Math.PI * 2,
      originalX: 0,
      popped: false,
      popAnimation: 0,
      isTarget: false,
      movementPattern: movementPattern,
      zigzagDirection: 1,
      zigzagCounter: 0,
    };
    
    // Mark as target if it matches the current target letter
    if (targetLetter && newBalloon.letter === targetLetter) {
      newBalloon.isTarget = true;
    }
    
    newBalloon.originalX = newBalloon.x;
    return newBalloon;
  }, [settings.speed, targetLetter, level]);

  const createParticles = useCallback((x, y, color, isTarget = false) => {
    const colors = isTarget 
      ? ["bg-warning", "bg-warning", "bg-warning", "bg-warning"] 
      : ["bg-warning", "bg-info", "bg-primary", "bg-success"];
    
    const count = isTarget ? 8 : 4;
    
    return Array.from({ length: count }, () => ({
      id: particleIdRef.current++,
      x: x,
      y: y,
      vx: (Math.random() - 0.5) * 6,
      vy: Math.random() * -4 - 2,
      color: colors[Math.floor(Math.random() * colors.length)],
      life: 25,
      maxLife: 25,
      isTarget: isTarget,
    }));
  }, []);

  const handleKeyPress = useCallback(
    (event) => {
      if (gameState !== "playing" || isPaused) return;
      const key = event.key.toUpperCase();
      if (!LETTERS.includes(key)) return;

      setBalloons((prev) => {
        const targetBalloon = prev.find((b) => b.letter === key && !b.popped);
        if (targetBalloon) {
          setScore((prevScore) => {
            const newScore = prevScore + 1 + (targetBalloon.isTarget ? 2 : 0);
            if (newScore > highScore && !settings.practiceMode) {
              setHighScore(newScore);
              localStorage.setItem("balloonHighScore", newScore.toString());
            }
            return newScore;
          });
          
          // Speak the letter
          speakLetter(key);
          
          // Handle combo
          setCombo((prevCombo) => {
            const newCombo = prevCombo + 1;
            if (newCombo > maxCombo) setMaxCombo(newCombo);
            
            // Show combo notification for combos of 5 or more
            if (newCombo >= 5) {
              setShowCombo(true);
              setComboPosition({ x: targetBalloon.x, y: targetBalloon.y });
              if (comboTimeoutRef.current) clearTimeout(comboTimeoutRef.current);
              comboTimeoutRef.current = setTimeout(() => setShowCombo(false), 1000);
            }
            
            return newCombo;
          });
          
          // Handle streak for target balloons
          if (targetBalloon.isTarget) {
            setStreak((prevStreak) => {
              const newStreak = prevStreak + 1;
              if (streakTimeoutRef.current) clearTimeout(streakTimeoutRef.current);
              streakTimeoutRef.current = setTimeout(() => setStreak(0), 3000);
              return newStreak;
            });
          } else {
            setStreak(0);
          }
          
          if (settings.soundEnabled && audioContextRef.current) {
            setTimeout(() => {
              playPopSound(audioContextRef.current, settings.volume);
            }, 100);
          }
          setParticles((p) => [
            ...p,
            ...createParticles(targetBalloon.x, targetBalloon.y, targetBalloon.color, targetBalloon.isTarget),
          ]);
          return prev.map((b) =>
            b.id === targetBalloon.id
              ? { ...b, popped: true, popAnimation: 30 }
              : b
          );
        } else {
          // Wrong key pressed - reset combo
          setCombo(0);
          setStreak(0);
        }
        return prev;
      });
    },
    [
      gameState,
      settings.soundEnabled,
      settings.volume,
      settings.practiceMode,
      createParticles,
      highScore,
      maxCombo,
      speakLetter,
      isPaused
    ]
  );

  const handleVirtualKeyPress = useCallback(
    (key) => {
      if (gameState !== "playing" || isPaused) return;

      setBalloons((prev) => {
        const targetBalloon = prev.find((b) => b.letter === key && !b.popped);
        if (targetBalloon) {
          setScore((prevScore) => {
            const newScore = prevScore + 1 + (targetBalloon.isTarget ? 2 : 0);
            if (newScore > highScore && !settings.practiceMode) {
              setHighScore(newScore);
              localStorage.setItem("balloonHighScore", newScore.toString());
            }
            return newScore;
          });
          
          // Speak the letter
          speakLetter(key);
          
          // Handle combo
          setCombo((prevCombo) => {
            const newCombo = prevCombo + 1;
            if (newCombo > maxCombo) setMaxCombo(newCombo);
            
            // Show combo notification for combos of 5 or more
            if (newCombo >= 5) {
              setShowCombo(true);
              setComboPosition({ x: targetBalloon.x, y: targetBalloon.y });
              if (comboTimeoutRef.current) clearTimeout(comboTimeoutRef.current);
              comboTimeoutRef.current = setTimeout(() => setShowCombo(false), 1000);
            }
            
            return newCombo;
          });
          
          // Handle streak for target balloons
          if (targetBalloon.isTarget) {
            setStreak((prevStreak) => {
              const newStreak = prevStreak + 1;
              if (streakTimeoutRef.current) clearTimeout(streakTimeoutRef.current);
              streakTimeoutRef.current = setTimeout(() => setStreak(0), 3000);
              return newStreak;
            });
          } else {
            setStreak(0);
          }
          
          if (settings.soundEnabled && audioContextRef.current) {
            setTimeout(() => {
              playPopSound(audioContextRef.current, settings.volume);
            }, 100);
          }
          setParticles((p) => [
            ...p,
            ...createParticles(targetBalloon.x, targetBalloon.y, targetBalloon.color, targetBalloon.isTarget),
          ]);
          return prev.map((b) =>
            b.id === targetBalloon.id
              ? { ...b, popped: true, popAnimation: 30 }
              : b
          );
        } else {
          // Wrong key pressed - reset combo
          setCombo(0);
          setStreak(0);
        }
        return prev;
      });
    },
    [
      gameState,
      settings.soundEnabled,
      settings.volume,
      settings.practiceMode,
      createParticles,
      highScore,
      maxCombo,
      speakLetter,
      isPaused
    ]
  );

  useEffect(() => {
    if (gameState !== "playing" || isPaused) {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
      return;
    }
    
    gameLoopRef.current = setInterval(() => {
      const currentTime = Date.now();
      
      // Update difficulty based on score
      const newDifficulty = Math.min(15, Math.floor(score / 15) + 1); // Slower progression
      if (newDifficulty !== difficultyLevelRef.current) {
        difficultyLevelRef.current = newDifficulty;
        setLevel(newDifficulty);
        
        // Set a new target letter when difficulty increases
        if (newDifficulty > 1) {
          setTargetLetter(LETTERS[Math.floor(Math.random() * LETTERS.length)]);
        }
        
        // Adjust balloon spawn count based on level
        if (newDifficulty > 10) {
          balloonSpawnCountRef.current = 2; // Reduced from 3
        } else if (newDifficulty > 5) {
          balloonSpawnCountRef.current = 1; // Reduced from 2
        } else {
          balloonSpawnCountRef.current = 1;
        }
      }
      
      setBalloons((prev) => {
        const updated = prev.map((balloon) => {
          if (balloon.popped) {
            return {
              ...balloon,
              popAnimation: Math.max(0, balloon.popAnimation - 1),
            };
          }
          
          const newWobble = balloon.wobble + 0.03; // Slower wobble
          let newY = balloon.y + balloon.speed;
          let newX = balloon.originalX;
          
          // Apply different movement patterns based on level
          if (balloon.movementPattern === "zigzag" && level > 8) {
            // Gentler zigzag movement for kids
            balloon.zigzagCounter += 0.05; // Slower zigzag
            newX = balloon.originalX + Math.sin(balloon.zigzagCounter) * 6 * balloon.zigzagDirection; // Reduced amplitude
            
            // Change direction occasionally
            if (Math.random() < 0.005) { // Less frequent direction changes
              balloon.zigzagDirection *= -1;
            }
          } else if (balloon.movementPattern === "wobble" && level > 4) {
            // Wobble movement for intermediate levels
            newX = balloon.originalX + Math.sin(newWobble) * 2; // Reduced wobble
          } else {
            // Normal movement
            newX = balloon.originalX + Math.sin(newWobble) * 1;
          }
          
          return { ...balloon, x: newX, y: newY, wobble: newWobble };
        });
        
        const filtered = updated.filter((balloon) => {
          if (balloon.popped && balloon.popAnimation <= 0) return false;
          if (!balloon.popped && balloon.y > 110) {
            if (!settings.practiceMode) {
              setLives((l) => l - 1);
            }
            setCombo(0); // Reset combo when balloon escapes
            return false;
          }
          return true;
        });
        
        return filtered;
      });

      setParticles((prev) => {
        return prev
          .map((particle) => ({
            ...particle,
            x: particle.x + particle.vx * 0.3,
            y: particle.y + particle.vy * 0.3,
            vy: particle.vy + 0.2,
            life: particle.life - 1,
          }))
          .filter((p) => p.life > 0);
      });

      // Improved balloon spawning logic with difficulty progression
      const timeSinceLastSpawn = currentTime - lastSpawnTimeRef.current;
      const baseSpawnInterval = 3000; // Increased from 2500 for slower spawn
      const minSpawnInterval = 2000; // Increased from 1500
      
      // Difficulty affects spawn rate (slower progression)
      const difficultyReduction = Math.min(1200, score * 15 + (difficultyLevelRef.current - 1) * 150); 
      const currentSpawnInterval = Math.max(minSpawnInterval, baseSpawnInterval - difficultyReduction);
      
      if (timeSinceLastSpawn >= currentSpawnInterval) {
        setBalloons((prev) => {
          // Limit maximum balloons on screen based on difficulty
          const activeBalloons = prev.filter(b => !b.popped);
          const maxBalloons = isMobile ? 
            (2 + Math.floor(difficultyLevelRef.current / 3)) : // Reduced max balloons
            (3 + Math.floor(difficultyLevelRef.current / 3)); // Reduced max balloons
          
          if (activeBalloons.length < maxBalloons) {
            lastSpawnTimeRef.current = currentTime;
            
            // Spawn multiple balloons at higher levels
            const newBalloons = [];
            for (let i = 0; i < balloonSpawnCountRef.current; i++) {
              if (activeBalloons.length + newBalloons.length < maxBalloons) {
                newBalloons.push(createBalloon());
              }
            }
            
            return [...prev, ...newBalloons];
          }
          return prev;
        });
      }
    }, 50);

    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [gameState, score, createBalloon, isMobile, level, isPaused, settings.practiceMode]);

  useEffect(() => {
    if (lives <= 0 && gameState === "playing" && !settings.practiceMode) {
      setGameState("gameOver");
    }
  }, [lives, gameState, settings.practiceMode]);

  useEffect(() => {
    const handleKeyDown = (e) => handleKeyPress(e);
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyPress]);

  const VirtualKeyboard = ({ onKeyPress }) => {
    // Split alphabet into rows for better mobile layout
    const row1 = LETTERS.slice(0, 7);
    const row2 = LETTERS.slice(7, 14);
    const row3 = LETTERS.slice(14, 21);
    const row4 = LETTERS.slice(21, 26);
    
    const buttonSize = isMobile ? "32px" : "50px";
    const fontSize = isMobile ? "0.9rem" : "1.2rem";
    
    return (
      <div className="position-fixed bottom-0 start-0 w-100 bg-white p-1 border-top shadow-lg" style={{ zIndex: 1000 }}>
        <div className="d-flex flex-column gap-1 align-items-center">
          {[row1, row2, row3, row4].map((row, rowIndex) => (
            <div key={rowIndex} className="d-flex gap-1 justify-content-center flex-wrap">
              {row.map((letter) => (
                <button
                  key={letter}
                  onClick={() => onKeyPress(letter)}
                  className={`btn rounded-3 d-flex align-items-center justify-content-center fw-bold ${
                    targetLetter === letter ? "btn-warning animate-pulse" : "btn-primary"
                  }`}
                  style={{ 
                    width: buttonSize, 
                    height: buttonSize, 
                    fontSize: fontSize,
                    minWidth: buttonSize,
                    padding: "0"
                  }}
                >
                  {letter}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const decorations = useMemo(() => {
    return Array.from({ length: isMobile ? 8 : 15 }).map((_, i) => ({
      key: i,
      emoji: ["🎈", "🌟", "⭐", "🎯", "🎪"][Math.floor(Math.random() * 5)],
      left: `${Math.random() * 90 + 5}%`,
      top: `${Math.random() * 90 + 5}%`,
      animationDelay: `${Math.random() * 3}s`,
      animationDuration: `${2 + Math.random()}s`,
    }));
  }, [isMobile]);

  // Custom gradient backgrounds
  const bgGradientMenu = {
    background: "linear-gradient(to bottom, #67e8f9, #3b82f6)",
  };
  const bgGradientSettings = {
    background: "linear-gradient(to bottom, #d1c4e9, #7e57c2)",
  };
  const bgGradientGameOver = {
    background: "linear-gradient(to bottom, #ffe082, #ef5350)",
  };
  const bgGradientPlaying = {
    background: "linear-gradient(to bottom, #81d4fa, #0288d1)",
  };

  // Responsive balloon sizing
  const balloonSize = isMobile ? { width: '50px', height: '60px' } : { width: '70px', height: '85px' };
  const balloonFontSize = isMobile ? '1.2rem' : '1.6rem';

  // Custom CSS for effects
  const style = `
    .balloon {
      width: ${balloonSize.width}; 
      height: ${balloonSize.height};
      border-radius: 50%;
      border: 2px solid rgba(255,255,255,0.4);
      cursor: pointer;
      transition: transform 0.1s;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .balloon:hover { transform: scale(1.05); }
    .balloon-target {
      animation: target-pulse 1.5s infinite;
      box-shadow: 0 0 15px 5px rgba(255,193,7,0.7);
    }
    @keyframes target-pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); }
    }
    .balloon-highlight {
      position: absolute;
      top: 6px; left: 6px;
      width: ${isMobile ? '12px' : '20px'}; 
      height: ${isMobile ? '15px' : '24px'};
      background: rgba(255,255,255,0.6);
      border-radius: 50%;
      filter: blur(2px);
    }
    .balloon-string {
      width: 2px; 
      height: ${isMobile ? '15px' : '28px'};
      background: #666;
      margin: 0 auto;
    }
    .animate-bounce {
      animation: bounce 1.6s infinite;
    }
    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-12px); }
    }
    .animate-pulse {
      animation: pulse 2s infinite;
    }
    @keyframes pulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(255,235,59,0.5); }
      50% { box-shadow: 0 0 20px 4px rgba(255,235,59,0.5);}
    }
    .text-shadow {
      text-shadow: 1px 1px 2px rgba(0,0,0,0.7);
    }
    .mobile-ui {
      font-size: ${isMobile ? '0.8rem' : '1rem'};
    }
    .mobile-button {
      width: ${isMobile ? '36px' : '48px'};
      height: ${isMobile ? '36px' : '48px'};
    }
    .combo-popup {
      animation: combo-pop 1s forwards;
      font-weight: bold;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
    }
    @keyframes combo-pop {
      0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
      50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
      100% { transform: translate(-50%, -50%) scale(1) translateY(-30px); opacity: 0; }
    }
    .bg-purple { background-color: #6f42c1 !important; }
    .bg-pink { background-color: #d63384 !important; }
    .level-indicator {
      background: linear-gradient(90deg, #4CAF50, #8BC34A, #CDDC39, #FFEB3B, #FFC107, #FF9800, #FF5722);
      background-size: 200% 100%;
      animation: gradient-shift 3s infinite alternate;
    }
    @keyframes gradient-shift {
      0% { background-position: 0% 50%; }
      100% { background-position: 100% 50%; }
    }
    .countdown {
      font-size: 6rem;
      text-shadow: 3px 3px 6px rgba(0,0,0,0.3);
      animation: countdown-pop 1s forwards;
    }
    @keyframes countdown-pop {
      0% { transform: scale(0.5); opacity: 0; }
      50% { transform: scale(1.2); opacity: 1; }
      100% { transform: scale(1); opacity: 0; }
    }
    .game-container {
      padding-bottom: ${isMobile && showKeyboard ? "140px" : "0px"};
      overflow: hidden;
    }
    @media (max-width: 576px) {
      .btn-md {
        padding: 0.375rem 0.75rem;
        font-size: 0.875rem;
      }
    }
  `;

  // MAIN MENU
  if (gameState === "menu") {
    return (
      <div
        className="w-100 vh-100 d-flex align-items-center justify-content-center position-relative overflow-hidden p-2"
        style={bgGradientMenu}
      >
        <style>{style}</style>
        {/* Floating decorations */}
        <div
          className="position-absolute w-100 h-100"
          style={{ pointerEvents: "none", left: 0, top: 0 }}
        >
          {decorations.map((dec) => (
            <div
              key={dec.key}
              className="position-absolute animate-bounce"
              style={{
                left: dec.left,
                top: dec.top,
                fontSize: isMobile ? "1.2rem" : "2rem",
                opacity: 0.7,
                animationDelay: dec.animationDelay,
                animationDuration: dec.animationDuration,
              }}
            >
              {dec.emoji}
            </div>
          ))}
        </div>
        {/* Main menu card */}
        <div
          className="bg-white rounded-4 p-3 p-md-4 shadow-lg mx-2 mx-md-3 text-center position-relative"
          style={{ zIndex: 2, maxWidth: isMobile ? "95%" : 400, width: "100%" }}
        >
          <div style={{ fontSize: isMobile ? "2.5rem" : "4rem", marginBottom: 20 }}>🎈</div>
          <h1
            className="fw-bold mb-3"
            style={{
              fontSize: isMobile ? "1.8rem" : "2.5rem",
              background: "linear-gradient(90deg,#7e57c2,#f06292)",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            Balloon Letter Pop!
          </h1>
          <p className={`text-secondary mb-3 ${isMobile ? 'fs-6' : 'fs-5'}`}>
            Pop balloons by pressing the right letters!
          </p>
          <div className={`mb-3 d-flex align-items-center justify-content-center gap-2 ${isMobile ? 'fs-6' : 'fs-5'} fw-bold text-warning`}>
            <Trophy size={isMobile ? 16 : 20} className="me-2" /> Best Score: {highScore}
          </div>
          <div className="d-grid gap-2 gap-md-3">
            <button
              onClick={() => {
                setSettings(s => ({...s, practiceMode: false}));
                resetGame();
              }}
              className={`btn btn-success ${isMobile ? 'btn-md' : 'btn-lg'} fw-bold d-flex align-items-center justify-content-center gap-2`}
            >
              <Play size={isMobile ? 16 : 24} /> Start Playing!
            </button>
            <button
              onClick={() => {
                setSettings(s => ({...s, practiceMode: true}));
                resetGame();
              }}
              className={`btn btn-info ${isMobile ? 'btn-md' : 'btn-lg'} fw-bold d-flex align-items-center justify-content-center gap-2`}
            >
              <BookOpen size={isMobile ? 16 : 20} /> Practice Mode
            </button>
            <button
              onClick={() => {
                setCameFrom("menu");
                setGameState("settings");
              }}
              className={`btn btn-primary ${isMobile ? 'btn-md' : 'btn-lg'} fw-bold d-flex align-items-center justify-content-center gap-2`}
            >
              <Settings size={isMobile ? 16 : 20} /> Settings
            </button>
          </div>
        </div>
      </div>
    );
  }

  // SETTINGS
  if (gameState === "settings") {
    return (
      <div
        className="w-100 vh-100 d-flex align-items-center justify-content-center p-2 p-md-3"
        style={bgGradientSettings}
      >
        <style>{style}</style>
        <div
          className="bg-white rounded-4 p-3 p-md-4 shadow-lg w-100"
          style={{ maxWidth: isMobile ? "95%" : 500 }}
        >
          <h2
            className={`fw-bold text-center mb-3 mb-md-4 d-flex align-items-center justify-content-center gap-2 text-purple ${isMobile ? 'fs-4' : 'fs-2'}`}
            style={{ color: "#7e57c2" }}
          >
            <Settings size={isMobile ? 20 : 32} /> Settings
          </h2>
          {/* Speed Setting */}
          <div className="mb-3 mb-md-4">
            <h3 className={`fw-bold mb-2 d-flex align-items-center gap-2 text-dark ${isMobile ? 'fs-5' : 'fs-4'}`}>
              <Zap size={16} /> Balloon Speed
            </h3>
            <div className="d-flex gap-1 gap-md-2 justify-content-between">
              {["Slow", "Medium", "Fast"].map((speed, index) => (
                <button
                  key={speed}
                  onClick={() =>
                    setSettings((s) => ({ ...s, speed: index + 1 }))
                  }
                  className={`btn ${isMobile ? 'btn-sm' : 'btn-lg'} fw-bold flex-fill ${
                    settings.speed === index + 1
                      ? "btn-success"
                      : "btn-outline-secondary"
                  }`}
                  style={
                    settings.speed === index + 1
                      ? { transform: "scale(1.05)" }
                      : {}
                  }
                >
                  {speed}
                </button>
              ))}
            </div>
          </div>
          {/* Sound Setting */}
          <div className="mb-3 mb-md-4">
            <h3 className={`fw-bold mb-2 d-flex align-items-center gap-2 text-dark ${isMobile ? 'fs-5' : 'fs-4'}`}>
              {settings.soundEnabled ? (
                <Volume2 size={16} />
              ) : (
                <VolumeX size={16} />
              )}
              Sound Effects
            </h3>
            <div className="mb-2">
              <button
                onClick={() =>
                  setSettings((s) => ({ ...s, soundEnabled: !s.soundEnabled }))
                }
                className={`btn ${isMobile ? 'btn-sm' : 'btn-lg'} fw-bold ${
                  settings.soundEnabled ? "btn-success" : "btn-secondary"
                }`}
              >
                {settings.soundEnabled ? "Sounds ON" : "Sounds OFF"}
              </button>
            </div>
            {settings.soundEnabled && (
              <div>
                <label className="form-label">
                  Volume: {Math.round(settings.volume * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={settings.volume}
                  onChange={(e) =>
                    setSettings((s) => ({
                      ...s,
                      volume: parseFloat(e.target.value),
                    }))
                  }
                  className="form-range"
                />
              </div>
            )}
          </div>
          {/* Voice Setting */}
          <div className="mb-3 mb-md-4">
            <h3 className={`fw-bold mb-2 d-flex align-items-center gap-2 text-dark ${isMobile ? 'fs-5' : 'fs-4'}`}>
              <Sparkles size={16} />
              Letter Voice
            </h3>
            <div className="mb-2">
              <button
                onClick={() =>
                  setSettings((s) => ({ ...s, voiceEnabled: !s.voiceEnabled }))
                }
                className={`btn ${isMobile ? 'btn-sm' : 'btn-lg'} fw-bold ${
                  settings.voiceEnabled ? "btn-success" : "btn-secondary"
                }`}
              >
                {settings.voiceEnabled ? "Voice ON" : "Voice OFF"}
              </button>
            </div>
            <small className="text-muted">
              {settings.voiceEnabled 
                ? "Letters will be spoken when balloons are popped" 
                : "Letters will not be spoken"}
            </small>
          </div>
          <button
            onClick={() => setGameState(cameFrom)}
            className={`btn btn-primary ${isMobile ? 'btn-md' : 'btn-lg'} w-100 fw-bold d-flex align-items-center justify-content-center gap-2`}
          >
            <Home size={16} /> Back
          </button>
        </div>
      </div>
    );
  }

  // GAME OVER
  if (gameState === "gameOver") {
    const isNewHigh = score > initialHighRef.current;
    return (
      <div
        className="w-100 vh-100 d-flex align-items-center justify-content-center p-2 p-md-3"
        style={bgGradientGameOver}
      >
        <style>{style}</style>
        <div
          className="bg-white rounded-4 p-3 p-md-4 shadow-lg w-100 text-center"
          style={{ maxWidth: isMobile ? "95%" : 400 }}
        >
          <div style={{ fontSize: isMobile ? "2rem" : "3rem", marginBottom: 20 }}>
            {score >= 20 ? "🏆" : score >= 10 ? "🌟" : "🎈"}
          </div>
          <h2 className={`fw-bold mb-2 text-danger ${isMobile ? 'fs-4' : 'fs-2'}`}>Great Job Playing!</h2>
          <div className={`${isMobile ? 'fs-5' : 'fs-4'} fw-bold mb-1 d-flex align-items-center justify-content-center gap-2 text-warning`}>
            <Trophy size={isMobile ? 18 : 24} className="me-2" />
            You popped {score} balloons!{" "}
            {isNewHigh && (
              <span className="text-success animate-pulse">New High!</span>
            )}
          </div>
          <div className="fw-bold text-secondary mb-2">
            High Score: {highScore}
          </div>
          {maxCombo >= 5 && (
            <div className="fw-bold text-info mb-2">
              Max Combo: {maxCombo}x!
            </div>
          )}
          <div className="fw-bold text-primary mb-2">
            Reached Level: {level}
          </div>
          <p className={`mb-3 mb-md-4 ${isMobile ? 'fs-6' : 'fs-5'} text-secondary`}>
            {score >= 20
              ? "Amazing! You're a letter champion!"
              : score >= 10
              ? "Fantastic job! Keep practicing!"
              : "Good try! Practice makes perfect!"}
          </p>
          <div className="d-grid gap-2">
            <button
              onClick={() => {
                setSettings(s => ({...s, practiceMode: false}));
                resetGame();
              }}
              className={`btn btn-success ${isMobile ? 'btn-md' : 'btn-lg'} fw-bold d-flex align-items-center justify-content-center gap-2`}
            >
              <Play size={16} /> Play Again!
            </button>
            <button
              onClick={() => {
                setSettings(s => ({...s, practiceMode: true}));
                resetGame();
              }}
              className={`btn btn-info ${isMobile ? 'btn-md' : 'btn-lg'} fw-bold d-flex align-items-center justify-content-center gap-2`}
            >
              <BookOpen size={16} /> Practice Mode
            </button>
            <button
              onClick={() => {
                setCameFrom("gameOver");
                setGameState("settings");
              }}
              className={`btn btn-warning ${isMobile ? 'btn-md' : 'btn-lg'} fw-bold d-flex align-items-center justify-content-center gap-2`}
            >
              <Settings size={16} /> Settings
            </button>
            <button
              onClick={() => setGameState("menu")}
              className={`btn btn-primary ${isMobile ? 'btn-md' : 'btn-lg'} fw-bold d-flex align-items-center justify-content-center gap-2`}
            >
              <Home size={16} /> Main Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  // COUNTDOWN SCREEN
  if (countdown > 0) {
    return (
      <div
        className="w-100 vh-100 d-flex align-items-center justify-content-center position-relative overflow-hidden"
        style={bgGradientPlaying}
      >
        <style>{style}</style>
        <div className="countdown text-white fw-bold">
          {countdown}
        </div>
      </div>
    );
  }

  // GAME SCREEN
  const keyboardHeight = isMobile && showKeyboard ? "140px" : "0px";
  
  return (
    <div
      ref={gameContainerRef}
      className="w-100 vh-100 position-relative overflow-hidden game-container"
      style={bgGradientPlaying}
    >
      <style>{style}</style>
      {/* Pause overlay */}
      {isPaused && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-dark bg-opacity-50" style={{zIndex: 100}}>
          <h2 className="text-white fw-bold mb-3 mb-md-4">Game Paused</h2>
          <button 
            className="btn btn-success btn-md btn-lg fw-bold d-flex align-items-center gap-2"
            onClick={() => setIsPaused(false)}
          >
            <Play size={16} /> Resume Game
          </button>
        </div>
      )}
      
      {/* Sun */}
      <div
        className="position-absolute"
        style={{ 
          top: isMobile ? 10 : 32, 
          right: isMobile ? 10 : 32, 
          width: isMobile ? 40 : 64, 
          height: isMobile ? 40 : 64 
        }}
      >
        <div className="rounded-circle bg-warning shadow animate-pulse w-100 h-100 position-relative">
          <div
            className="rounded-circle bg-warning-subtle position-absolute"
            style={{ inset: isMobile ? 4 : 8 }}
          ></div>
        </div>
      </div>
      
      {/* Combo Indicator */}
      {showCombo && (
        <div
          className="position-absolute text-warning fw-bold combo-popup"
          style={{
            left: `${comboPosition.x}%`,
            top: `${comboPosition.y}%`,
            fontSize: isMobile ? '1.2rem' : '2rem',
            zIndex: 25,
          }}
        >
          {combo}x COMBO!
        </div>
      )}
      
      {/* Target Letter Indicator */}
      {targetLetter && difficultyLevelRef.current > 1 && (
        <div className="position-absolute top-0 start-50 translate-middle-x mt-3 bg-white bg-opacity-90 rounded-3 p-1 p-md-2 shadow d-flex align-items-center gap-1 gap-md-2 flex-wrap justify-content-center">
          <Target size={14} className="text-danger" />
          <span className="fw-bold">Target: </span>
          <span className="text-warning fw-bold fs-6 fs-md-5">{targetLetter}</span>
          <span className="text-info small">(+2 points)</span>
          {streak > 0 && (
            <span className="text-success fw-bold small">Streak: {streak}</span>
          )}
        </div>
      )}
      
      {/* Level Indicator */}
      <div className="position-absolute top-0 start-50 translate-middle-x mt-4 mt-md-5 pt-3 pt-md-4 bg-white bg-opacity-90 rounded-3 p-1 px-2 px-md-3 shadow-sm level-indicator">
        <span className="fw-bold text-white small">Level: {level}</span>
      </div>

      {/* Practice Mode Indicator */}
      {settings.practiceMode && (
        <div className="position-absolute top-0 start-0 mt-2 ms-2 ms-md-3 bg-info bg-opacity-90 rounded-3 p-1 px-2 shadow-sm">
          <span className="fw-bold text-white small">Practice Mode</span>
        </div>
      )}

      {/* Balloons */}
      {balloons.map((balloon) => (
        <div
          key={balloon.id}
          className="position-absolute"
          style={{
            left: `${balloon.x}%`,
            top: `${balloon.y}%`,
            transform: "translate(-50%, -50%)",
            transition: "all 0.1s ease-out",
            zIndex: 10,
          }}
        >
          {balloon.popped ? (
            balloon.popAnimation > 20 && (
              <div className="position-relative">
                <div className={`fw-bold text-warning animate-bounce ${isMobile ? 'fs-6' : 'fs-4'}`}>
                  POP! ⭐
                </div>
              </div>
            )
          ) : (
            <div className="position-relative">
              <div className={`balloon ${balloon.color} ${balloon.isTarget ? 'balloon-target' : ''}`}>
                <div className="balloon-highlight"></div>
                <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center">
                  <span className="text-white fw-bold text-shadow" style={{ fontSize: balloonFontSize }}>
                    {balloon.letter}
                  </span>
                </div>
              </div>
              <div className="balloon-string"></div>
            </div>
          )}
        </div>
      ))}
      
      {/* Particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className={`position-absolute rounded-circle ${particle.color} ${particle.isTarget ? 'animate-pulse' : ''}`}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: isMobile ? "4px" : "8px",
            height: isMobile ? "4px" : "8px",
            opacity: particle.life / particle.maxLife,
            zIndex: 20,
          }}
        />
      ))}
      
      {/* UI Elements */}
      <div className={`position-absolute top-0 start-0 m-${isMobile ? '1' : '4'} bg-white bg-opacity-95 rounded-3 p-1 p-md-2 shadow-sm`}>
        <div className="d-flex align-items-center gap-1 gap-md-2">
          <Star className="text-warning" size={isMobile ? 14 : 20} />
          <span className={`fw-bold text-dark mobile-ui ${isMobile ? 'fs-6' : 'fs-5'}`}>Score: {score}</span>
        </div>
        {combo >= 3 && (
          <div className="text-info fw-bold small">Combo: {combo}x</div>
        )}
      </div>
      
      {!settings.practiceMode && (
        <div
          className={`position-absolute top-0 end-0 m-${isMobile ? '1' : '4'} bg-white bg-opacity-95 rounded-3 p-1 p-md-2 shadow-sm`}
        >
          <div className="d-flex align-items-center gap-1 gap-md-2">
            <Heart className="text-danger" size={isMobile ? 12 : 18} />
            <span className={`fw-bold text-dark mobile-ui ${isMobile ? 'fs-6' : 'fs-6'}`}>Lives: {lives}</span>
          </div>
        </div>
      )}
      
      {showInstructions && (
        <div
          className={`position-absolute bottom-0 start-50 translate-middle-x mb-${isMobile ? '2' : '4'} bg-white bg-opacity-95 rounded-3 p-2 p-md-3 shadow text-center animate-bounce`}
          style={{ 
            maxWidth: isMobile ? 250 : 300, 
            zIndex: 30,
            marginBottom: isMobile && showKeyboard ? `${parseInt(keyboardHeight) + 10}px !important` : undefined
          }}
        >
          <p className={`fw-bold text-dark mb-1 ${isMobile ? 'fs-6' : 'fs-5'}`}>
            🎈 {isMobile ? 'Tap' : 'Press'} the letter on the balloon to pop it! 🎈
          </p>
          <p className={`text-secondary ${isMobile ? 'small' : 'fs-6'}`}>Try letters A through Z!</p>
        </div>
      )}
      
      {/* Pause button */}
      <button
        onClick={() => setIsPaused(!isPaused)}
        className="position-absolute bottom-0 end-0 btn btn-dark rounded-circle shadow mobile-button"
        title={isPaused ? "Resume" : "Pause"}
        style={{
          margin: isMobile ? '8px' : '16px',
          zIndex: 30,
          bottom: isMobile && showKeyboard ? keyboardHeight : "0",
        }}
      >
        {isPaused ? <Play size={isMobile ? 12 : 18} /> : <Pause size={isMobile ? 12 : 18} />}
      </button>
      
      {/* Home button */}
      <button
        onClick={() => setGameState("menu")}
        className="position-absolute bottom-0 start-0 btn btn-primary rounded-circle shadow mobile-button"
        title="Main Menu"
        style={{
          margin: isMobile ? '8px' : '16px',
          zIndex: 30,
          bottom: isMobile && showKeyboard ? keyboardHeight : "0",
        }}
      >
        <Home size={isMobile ? 12 : 18} />
      </button>
      
      {/* Restart button */}
      <button
        onClick={resetGame}
        className="position-absolute bottom-0 start-50 translate-middle-x btn btn-warning rounded-circle shadow mobile-button"
        title="Restart Game"
        style={{
          margin: isMobile ? '8px' : '16px',
          zIndex: 30,
          bottom: isMobile && showKeyboard ? keyboardHeight : "0",
        }}
      >
        <RotateCcw size={isMobile ? 12 : 18} />
      </button>
      
      {/* Keyboard toggle button for mobile */}
      {isMobile && (
        <button
          onClick={() => setShowKeyboard(!showKeyboard)}
          className="position-absolute top-0 end-0 btn btn-info rounded-circle shadow mobile-button mt-1 me-1"
          title="Toggle Keyboard"
          style={{
            zIndex: 30,
          }}
        >
          <Keyboard size={12} />
        </button>
      )}
      
      {/* Virtual Keyboard for mobile */}
      {isMobile && showKeyboard && (
        <VirtualKeyboard onKeyPress={handleVirtualKeyPress} />
      )}
    </div>
  );
};

export default BalloonLetterPop;